import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { Router } from '@angular/router';
import { MessageService, SortEvent } from 'primeng/api';
import { ContaService } from '../../../../services/conta/conta.service';
import { ContaDTO, NovaContaFormDTO } from '../../../../models/gerenciar-contas/conta.model';
import { Subscription, debounceTime, distinctUntilChanged, firstValueFrom, Subject } from 'rxjs';
import { Moeda, MOEDA_CONFIG } from '../../../../enums/moeda.enum';
import { TranslateService } from '@ngx-translate/core';
import { EmpresaConfigDTO, MoedaDTO } from '../../../../models/configuracoes/empresa.model';
import { TipoConta } from '../../../../enums/TipoConta.enum';
import { MovimentoService } from '../../../../services/movimento/movimento.service';
import { MovimentoDTO } from '../../../../models/gerenciar-contas/movimento.model';
import { CotacaoGuardService } from '../../../../services/cotacao/cotacao-guard.service';
import { CotacaoDTO } from '../../../../models/movimentacao.dto';
import { EmpresaConfigService } from '../../../../services/configuracoes/empresa.service';
import { TableColumn } from '../../../../models/shared/table-column.model';
import { moedasCompativeis } from '../../../../utils/moeda-pareamento.util';
import { toLocalISOString } from '../../../../shared/utils/date-utils';


@Component({
  selector: 'app-contas',
  templateUrl: './contas.component.html',
  styleUrls: ['./contas.component.scss'],
  providers: [MessageService],
})
export class ContasComponent implements OnInit, OnDestroy {
  // Controle de abas (0 = Cadastro, 1 = Lista)
  activeTabIndex: number = 1;

  deleteDialogVisible = false;
  deleteItemId: number | null = null;

  form: NovaContaFormDTO = this.createEmptyForm();
  // String exibida no input "Saldo Inicial" — preenchida pelo handler
  // formataCampoValor (estilo calculadora: digita "12" -> "0,12").
  montanteStr = '';
  submitting = false;
  contas: ContaDTO[] = [];
  totalRecords: number = 0;
  first: number = 0;
  rows: number = 10;
  loading: boolean = true;
  moeda: { label: string; value: Moeda; flagUrl: string }[] = [];
  lastfield: string = 'id';
  lastSort: string = 'asc';
  rows3 = 10;
  first3 = 0;
  pageSizeOptions = [
    { label: 5, value: 5 },
    { label: 10, value: 10 },
    { label: 20, value: 20 },
    { label: 120, value: 120 },
  ];
  contaEdicao: ContaDTO | null = null;
  removeInputsEdicaoBanco: boolean = false;
  tipoConta = TipoConta;
  tipoContaOptions: { label: string; value: TipoConta }[] = [];
  empresa: EmpresaConfigDTO | null = null;

  searchTerm: string = '';
  private search$ = new Subject<string>();

  /** Guarda o ultimo valor de "rows" processado pelo onRowsPerPageChange,
   *  para evitar chamadas duplicadas de loadContas quando o (ngModelChange)
   *  do dropdown de itens por pagina dispara no bootstrap com o valor atual. */
  private _lastRows: number = 10;

  // Configuracao de colunas visiveis
  readonly STORAGE_KEY = 'contas-visible-columns';

  columns: TableColumn[] = [
    { key: 'id', labelKey: 'contas.labels.id', visible: true, fixed: true, defaultVisible: true },
    { key: 'sobre', labelKey: 'contas.labels.sobre', visible: true, fixed: true, defaultVisible: true },
    { key: 'totalEntrada', labelKey: 'contas.labels.total_entrada', visible: true, fixed: false, defaultVisible: true },
    { key: 'totalSaida', labelKey: 'contas.labels.total_saida', visible: true, fixed: false, defaultVisible: true },
    { key: 'saldo', labelKey: 'contas.labels.saldo', visible: true, fixed: false, defaultVisible: true },
    { key: 'dataInclusao', labelKey: 'contas.labels.data_inclusao', visible: true, fixed: false, defaultVisible: true },
    { key: 'acoes', labelKey: 'contas.labels.acoes', visible: true, fixed: true, defaultVisible: true },
  ];

  // Objetos de traducao (padrao moderno - tipo literal implicito)
  texts = {
    titulo: '',
    subtitulo: '',
    titulo_criar: '',
    subtitulo_criar: '',
    titulo_editar: '',
    subtitulo_editar: '',
    aba_cadastro: '',
    aba_lista: '',
    lista_de_contas: '',
    nenhuma_conta_encontrada: '',
    ajuste_filtros: '',
    carregando: '',
    confirmar_exclusao: '',
    confirmacao: ''
  };

  labels = {
    id: '', sobre: '', total_entrada: '', total_saida: '',
    saldo: '', data_inclusao: '', acoes: '', itens_por_pagina: '',
    tipo: '', titulo: '', descricao: ''
  };

  placeholderPesquisar = '';

  botoes = {
    nova_conta: '', confirmar: '', cancelar: ''
  };

  tooltips = {
    ver_movimentos: '', editar: '', excluir: ''
  };

  private langChangeSub!: Subscription;

  constructor(
    private contaService: ContaService,
    private messageService: MessageService,
    private router: Router,
    private translate: TranslateService,
    private movimentoService: MovimentoService,
    private cotacaoGuard: CotacaoGuardService,
    private empresaService: EmpresaConfigService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit() {
    this.loadColumnPreferences();
    this.loadTranslations();
    this.search$
      .pipe(debounceTime(400), distinctUntilChanged())
      .subscribe((term) => {
        this.first = 0;
        this.first3 = 0;
        this.loadContas(0, this.rows, this.getCurrentSort());
      });

    this.moeda = this.getDropItems(Moeda);
    this.buildTipoContaOptions();
    this.form = this.createEmptyForm();
    this.loadContas(0, this.rows);
    this.findEmpresa();

    // Monitorar mudanca de idioma
    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      this.loadTranslations();
      this.buildTipoContaOptions();
    });
  }

  ngOnDestroy(): void {
    if (this.langChangeSub) {
      this.langChangeSub.unsubscribe();
    }
  }

  // =========================================================================
  // Traducoes
  // =========================================================================

  private loadTranslations(): void {
    const isEdicao = !!this.form.id;
    const isListaAtiva = this.activeTabIndex === 1;

    this.texts = {
      titulo: this.translate.instant(
        isListaAtiva ? 'contas.texts.titulo' : isEdicao ? 'contas.texts.titulo_editar' : 'contas.texts.titulo'
      ),
      subtitulo: this.translate.instant(
        isListaAtiva
          ? 'contas.texts.subtitulo_lista'
          : isEdicao
          ? 'contas.texts.subtitulo_editar'
          : 'contas.texts.subtitulo_criar'
      ),
      titulo_criar: this.translate.instant('contas.texts.titulo_criar'),
      subtitulo_criar: this.translate.instant('contas.texts.subtitulo_criar'),
      titulo_editar: this.translate.instant('contas.texts.titulo_editar'),
      subtitulo_editar: this.translate.instant('contas.texts.subtitulo_editar'),
      aba_cadastro: this.translate.instant('contas.texts.aba_cadastro'),
      aba_lista: this.translate.instant('contas.texts.aba_lista'),
      lista_de_contas: this.translate.instant('contas.texts.lista_de_contas'),
      nenhuma_conta_encontrada: this.translate.instant('contas.texts.nenhuma_conta_encontrada'),
      ajuste_filtros: this.translate.instant('contas.texts.ajuste_filtros'),
      carregando: this.translate.instant('contas.texts.carregando'),
      confirmar_exclusao: this.translate.instant('contas.mensagens.confirmar_exclusao'),
      confirmacao: this.translate.instant('common.confirmacao')
    };

    this.labels = {
      id: this.translate.instant('contas.labels.id'),
      sobre: this.translate.instant('contas.labels.sobre'),
      total_entrada: this.translate.instant('contas.labels.total_entrada'),
      total_saida: this.translate.instant('contas.labels.total_saida'),
      saldo: this.translate.instant('contas.labels.saldo'),
      data_inclusao: this.translate.instant('contas.labels.data_inclusao'),
      acoes: this.translate.instant('contas.labels.acoes'),
      itens_por_pagina: this.translate.instant('contas.labels.itens_por_pagina'),
      tipo: this.translate.instant('contas.labels.tipo'),
      titulo: this.translate.instant('contas.labels.titulo'),
      descricao: this.translate.instant('contas.labels.descricao')
    };

    this.placeholderPesquisar = this.translate.instant('contas.placeholders.buscar_por_titulo_da_conta');

    this.botoes = {
      nova_conta: this.translate.instant('contas.botoes.nova_conta'),
      confirmar: this.translate.instant('common.confirmar'),
      cancelar: this.translate.instant('common.cancelar')
    };

    this.tooltips = {
      ver_movimentos: this.translate.instant('contas.tooltips.ver_movimentos'),
      editar: this.translate.instant('contas.tooltips.editar'),
      excluir: this.translate.instant('contas.tooltips.excluir')
    };
  }

  novaConta(): void {
    this.limparForm();
    this.texts.titulo = this.translate.instant('contas.texts.titulo');
    this.texts.subtitulo = this.translate.instant('contas.texts.subtitulo_criar');
    this.activeTabIndex = 0;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  onTabChange(event: { index: number }): void {
    // index 0 = Cadastro, index 1 = Lista de Contas
    if (event.index === 1) {
      this.texts.titulo = this.translate.instant('contas.texts.titulo');
      this.texts.subtitulo = this.translate.instant('contas.texts.subtitulo_lista');
    } else {
      const isEdicao = !!this.form.id;
      this.texts.titulo = this.translate.instant(
        isEdicao ? 'contas.texts.titulo_editar' : 'contas.texts.titulo'
      );
      this.texts.subtitulo = this.translate.instant(
        isEdicao ? 'contas.texts.subtitulo_editar' : 'contas.texts.subtitulo_criar'
      );
    }
  }

  findEmpresa() {
    this.empresaService.getById('/configuracaoEmpresa/sessao').subscribe({
      next: (response) => {
        this.empresa = response;
        // Re-monta dropdown de moeda ja com filtro de pareamento aplicado
        // (regra do tech lead: empresa REAL/DOLAR nao ve PESO; empresa PESO
        // so ve GUARANI e PESO).
        this.moeda = this.getDropItems(Moeda).filter(item =>
          this.moedasPermitidas.has(String(item.value).toUpperCase())
        );
        // Recarrega a lista de contas agora que a empresa esta resolvida,
        // para que o filtro de moedasPermitidas seja aplicado corretamente.
        // O primeiro loadContas() em ngOnInit pode ter rodado antes da empresa
        // chegar (race condition) e usado o fallback de 4 moedas.
        const page = this.first / this.rows;
        this.loadContas(page, this.rows, this.getCurrentSort());
      },
      error: (error) => {
        console.error("Erro ao buscar empresa:", error);
      }
    });
  }

  /**
   * Conjunto de moedas permitidas para a empresa logada, baseado na
   * matriz de pareamento (moedasCompativeis). Usado para filtrar o
   * dropdown de moedas no cadastro de conta.
   *
   * Fallback: enquanto a empresa nao carregou, retorna todas as 4
   * moedas para nao bloquear a UI. O filtro fica estavel apos a
   * primeira resposta de getSessao().
   */
  get moedasPermitidas(): Set<string> {
    const moedaEmp = this.empresa?.moeda
      ? ((this.empresa.moeda as any).enum ?? this.empresa.moeda) as string
      : null;
    if (!moedaEmp) return new Set(['GUARANI', 'DOLAR', 'REAL', 'PESO']);
    return new Set(moedasCompativeis(moedaEmp as any));
  }

  loadContas(page: number, size: number, sort: string = 'id,asc') {
    this.loading = true;

    const termoBusca = this.searchTerm ? this.searchTerm.trim() : '';

    // Quando há termo de busca, usa o endpoint de busca por título (mesmo do app mobile)
    // GET /conta/titulodaconta?tituloDaConta=<termo>&page=&size=&sort=
    // Quando vazio, usa a listagem normal GET /conta com paginação
    const endpoint = termoBusca.length > 0 ? '/conta/titulodaconta' : '/conta';
    const params: any = termoBusca.length > 0
      ? { tituloDaConta: termoBusca, page, size, sort }
      : { page, size, sort };

    this.contaService.get(endpoint, params).subscribe({
      next: (response: any) => {
        if (response && response.content) {
          // Filtra contas pelas moedas permitidas conforme moeda padrao da empresa.
          // Spec do tech lead: empresa PESO so ve GUARANI/PESO; empresa REAL/DOLAR
          // nao ve PESO. Mesmo que existam contas legacy em moedas incompativeis,
          // elas ficam ocultas da listagem.
          // Observacao: a paginacao e server-side, portanto algumas paginas podem
          // exibir menos itens do que o "size" solicitado. Otimizacao futura:
          // mover filtro para o backend.
          const permitidas = this.moedasPermitidas;
          this.contas = (response.content as ContaDTO[]).filter(conta =>
            permitidas.has(this.asEnumString((conta as any).moeda))
          );
          this.totalRecords = response.totalElements || response.content.length;
        } else {
          this.contas = [];
          this.totalRecords = 0;
        }

        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.erro_servidor'),
        });
      },
    });
  }

  onPageChange(event: any) {
    this.first = event.first;
    this.rows = event.rows;
    const page = event.page;
    const size = event.rows;
    this.loadContas(page, size, this.getCurrentSort());
  }

  onSearchChange(value: string) {
    this.searchTerm = value || '';
    this.search$.next(this.searchTerm);
  }

  editarConta(conta: ContaDTO) {
    this.contaEdicao = conta;

    if (this.asEnumString(this.contaEdicao?.tipoConta) === 'BANCO') {
      this.removeInputsEdicaoBanco = true;
    }

    const tipoContaEnum = this.asEnumString(conta.tipoConta);
    this.form = {
      id: conta.id,
      tipoConta: this.tipoContaOptions.find(t => t.value === tipoContaEnum),
      descricao: conta.descricao || '',
      moeda: this.moeda.find(m => m.value === this.asMoedaEnum(conta.moeda)),
      numeroConta: conta.numeroConta,
      saldoInicial: conta.saldo,
      tituloDaConta: conta.tituloDaConta,
      aceitaTarjetaQrcode: conta.aceitaTarjetaQrcode === true,
    };
    // Reflete o saldo carregado na string de exibicao do input "Saldo Inicial".
    this.montanteStr = this.formatarMontanteParaExibicao(this.form.saldoInicial);

    // Atualizar titulo para modo edicao
    this.texts.titulo = this.translate.instant('contas.texts.titulo_editar');
    this.texts.subtitulo = this.translate.instant('contas.texts.subtitulo_editar');

    // Trocar para aba de cadastro com os dados preenchidos
    this.activeTabIndex = 0;
    this.cdr.detectChanges();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  navegarParaMovimentos(conta: ContaDTO): void {
    this.router.navigate(['/lista-de-movimentos'], {
      queryParams: { contaId: conta.id }
    });
  }

  deletarConta(id: number) {
    this.deleteItemId = id;
    this.deleteDialogVisible = true;
  }

  confirmarExclusao() {
    if (this.deleteItemId == null) return;
    this.contaService.delete(`/conta/${this.deleteItemId}`).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('common.sucesso'),
          detail: this.translate.instant('Conta_excluida_com_sucesso'),
        });
        const page = this.first / this.rows;
        this.loadContas(page, this.rows, this.getCurrentSort());
        this.deleteDialogVisible = false;
        this.deleteItemId = null;
      },
      error: () => {
        this.deleteDialogVisible = false;
        this.deleteItemId = null;
      },
    });
  }

  getDropItems(enumType: any): { label: string; value: any; flagUrl: string }[] {
    return Object.values(enumType).map((item) => ({
      label: String(item).toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
      value: item,
      flagUrl: this.getFlagUrl(item),
    }));
  }

  getFlagUrl(moeda: any): string {
    const moedaStr = String(moeda || '').toUpperCase();
    switch (moedaStr) {
      case 'GUARANI': return 'assets/images/paraguay-flag.jpg';
      case 'REAL': return 'assets/images/br-flag.svg';
      case 'DOLAR': return 'assets/images/usa-flag.svg';
      case 'PESO': return 'assets/images/argentina-flag.svg';
      default: return '';
    }
  }

  customSort(event: SortEvent): void {
    if (!event.field || !event.data) return;

    const field = event.field;
    const order = event.order ?? 1;

    // Verifica se o sort mudou para evitar chamada desnecessaria
    const newSort = order === 1 ? 'asc' : 'desc';
    const sortChanged = this.lastfield !== field || this.lastSort !== newSort;

    // Atualiza lastfield/lastSort para manter ordenacao em buscas futuras
    this.lastfield = field;
    this.lastSort = newSort;

    // Sort local para UX responsiva
    event.data.sort((a: any, b: any) => {
      const valueA = this.getSortValue(a, field);
      const valueB = this.getSortValue(b, field);

      if (valueA == null && valueB == null) return 0;
      if (valueA == null) return -1 * order;
      if (valueB == null) return 1 * order;

      if (typeof valueA === 'string' && typeof valueB === 'string') {
        return valueA.localeCompare(valueB) * order;
      }

      return (valueA < valueB ? -1 : valueA > valueB ? 1 : 0) * order;
    });

    // Buscar do backend com a ordenacao correta (evita loop infinito)
    if (sortChanged) {
      const page = this.first / this.rows;
      this.loadContas(page, this.rows, this.getCurrentSort());
    }
  }

  /** Retorna a string de sort atual no formato 'campo,direcao' para o backend */
  private getCurrentSort(): string {
    const backendField = this.mapSortFieldToBackend(this.lastfield);
    return `${backendField},${this.lastSort}`;
  }

  /** Mapeia o campo da tabela frontend para o campo da entidade no backend */
  private mapSortFieldToBackend(field: string): string {
    switch (field) {
      case 'sobre': return 'tituloDaConta';
      case 'totalEntrada': return 'valorTotalDeEntradas';
      case 'totalSaida': return 'valorTotalDeSaidas';
      case 'saldo': return 'saldo';
      case 'dataInclusao': return 'criadoEm';
      case 'id': return 'id';
      default: return field;
    }
  }

  private getSortValue(conta: any, field: string): any {
    switch (field) {
      case 'id':
        return conta.id;
      case 'sobre':
        return conta.tituloDaConta;
      case 'totalEntrada':
        return conta.valorTotalDeEntradas;
      case 'totalSaida':
        return conta.valorTotalDeSaidas;
      case 'saldo':
        return conta.saldo;
      case 'dataInclusao':
        return conta.criadoEm ? new Date(conta.criadoEm) : null;
      default:
        return conta[field];
    }
  }

  formatarDataEHora(dataISO: string): string {
    const data = new Date(dataISO);
    if (isNaN(data.getTime())) return '';
    const dia = String(data.getDate()).padStart(2, '0');
    const mes = String(data.getMonth() + 1).padStart(2, '0');
    const ano = data.getFullYear();
    const horas = String(data.getHours()).padStart(2, '0');
    const minutos = String(data.getMinutes()).padStart(2, '0');
    return `${dia}/${mes}/${ano} ${horas}:${minutos}`;
  }

  isColumnVisible(key: string): boolean {
    const col = this.columns.find(c => c.key === key);
    return col ? col.visible : true;
  }

  get visibleColumnCount(): number {
    return this.columns.filter(c => c.visible).length;
  }

  onColumnsChange(columns: TableColumn[]): void {
    this.columns = [...columns];
  }

  private loadColumnPreferences(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const visibleKeys = JSON.parse(stored) as string[];
        this.columns.forEach(col => {
          if (!col.fixed) {
            col.visible = visibleKeys.includes(col.key);
          }
        });
      }
    } catch (e) {
      console.warn('Erro ao carregar preferências de colunas:', e);
    }
  }

  onRowsPerPageChange() {
    // Guard anti-reentrada: o (ngModelChange) do dropdown de itens por pagina
    // dispara no bootstrap do PrimeNG com o valor atual, gerando uma chamada
    // duplicada de loadContas alem da feita em ngOnInit. Ignorar quando
    // "rows" nao mudou de valor.
    if (this.rows === this._lastRows) return;
    this._lastRows = this.rows;
    this.first = 0;
    this.loadContas(0, this.rows, this.getCurrentSort());
  }

  // Verifica se é conta padrão do sistema (criada automaticamente para cada moeda)
  isContaPadrao(conta: ContaDTO): boolean {
    const t: any = (conta as any)?.tipoConta;
    const tipoEnum = t?.enum ?? t;
    return tipoEnum === TipoConta.DINHEIRO;
  }

  // Mostra "DINHEIRO" / "BANCO" etc, evitando [object Object]
  tipoEnum(conta: ContaDTO): string {
    const t: any = (conta as any)?.tipoConta;
    return t?.enum ?? (t ?? '');
  }

  // Retorna código ISO da moeda para o pipe currency (ex: PYG, BRL, USD)
  getCurrencyCode(conta: ContaDTO): string {
    const m: any = (conta as any)?.moeda;
    const enumStr = String(m?.enum ?? m ?? '').toUpperCase();
    const moedaEnum = enumStr as Moeda;
    return MOEDA_CONFIG[moedaEnum]?.symbol ?? 'PYG';
  }

  // Retorna dígitos do currency pipe conforme moeda
  getCurrencyDigits(conta: ContaDTO): string {
    const m: any = (conta as any)?.moeda;
    const enumStr = String(m?.enum ?? m ?? '').toUpperCase();
    const moedaEnum = enumStr as Moeda;
    return MOEDA_CONFIG[moedaEnum]?.digits ?? '1.0-0';
  }

  // Retorna locale conforme moeda para separadores corretos
  getCurrencyLocale(conta: ContaDTO): string {
    const m: any = (conta as any)?.moeda;
    const enumStr = String(m?.enum ?? m ?? '').toUpperCase();
    const moedaEnum = enumStr as Moeda;
    return MOEDA_CONFIG[moedaEnum]?.locale ?? 'es-PY';
  }

  get isFormDirty(): boolean {
    const empty = this.createEmptyForm();
    return (
      this.form.tituloDaConta !== empty.tituloDaConta ||
      this.form.numeroConta !== empty.numeroConta ||
      this.form.descricao !== empty.descricao ||
      this.form.saldoInicial !== empty.saldoInicial ||
      this.form.moeda !== empty.moeda ||
      this.form.tipoConta?.value !== empty.tipoConta?.value
    );
  }

  validaCampos(): boolean {
    if (
      !this.form.tipoConta ||
      !this.form.moeda ||
      (!this.form.saldoInicial && this.form.saldoInicial !== 0) ||
      (!this.form.tituloDaConta || this.form.tituloDaConta.length < 3) ||
      !this.form.numeroConta
    ) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('common.atencao'),
        detail: (this.form.tituloDaConta.length < 3 && this.form.tituloDaConta != "") ? this.translate.instant('Titulo_da_conta_deve_ter_pelo_menos_3_caracteres') : this.translate.instant('Todos_os_campos_sao_Obrigatorios'),
      });
      return false;
    }
    return true;
  }

  async submit() {
    if (this.submitting) return;
    if (!this.validaCampos()) {
      return;
    }

    // Saldo inicial gera movimento e exige cotacao do dia: solicita ANTES de criar a
    // conta, para nao deixar a conta criada sem o movimento inicial.
    if ((this.form.saldoInicial || 0) !== 0) {
      this.submitting = true;
      const cotacaoDia = await this.cotacaoGuard.garantirCotacao({ moeda: this.getEmpresaMoedaPadrao() });
      if (!cotacaoDia) {
        this.submitting = false;
        return;
      }
      this.submitting = false;
    }
    const conta: any = {
      tipoConta: this.form.tipoConta?.value,
      moeda: this.form.moeda?.value,
      tituloDaConta: this.form.tituloDaConta,
      numeroConta: this.form.numeroConta,
      saldo: this.form.saldoInicial || 0,
      valorTotalDeEntradas: 0,
      valorTotalDeSaidas: 0,
      descricao: this.form.descricao || this.form.tituloDaConta,
      aceitaTarjetaQrcode: this.form.aceitaTarjetaQrcode,
      // Os dados bancarios da empresa nao sao mostrados ao cliente no Mini Shop.
      exibirParaTransferencia: false,
    };

    this.submitting = true;
    this.contaService.post('/conta', conta).subscribe({
      next: (contaCriada: ContaDTO) => {
        this.submitting = false;
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('common.sucesso'),
          detail: this.translate.instant('Conta_criada_com_sucesso'),
        });

        // Captura o saldo antes de limpar o form, pois limparForm() zera o campo
        const saldoInicial = this.form.saldoInicial || 0;
        this.limparForm();

        // Só cria movimento inicial se houver saldo diferente de zero
        if (saldoInicial !== 0) {
          this.movimentoInicialConta(contaCriada, saldoInicial)
            .then(() => {
              this.loadContas(0, this.rows, this.getCurrentSort());
              this.activeTabIndex = 1;
            });
        } else {
          this.loadContas(0, this.rows, this.getCurrentSort());
          this.activeTabIndex = 1;
        }
      },
      error: () => {
        this.submitting = false;
      },
    });
  }

  validaMoedaValor(moeda: Moeda): number {
    const valor = this.form.saldoInicial || 0;
    valor > 0 ? valor : valor * -1;

    if (moeda == Moeda.DOLAR) {
      return valor;
    } else if (moeda == Moeda.GUARANI) {
      return valor;
    }
    return valor;
  }

  async movimentoInicialConta(conta: ContaDTO, valor: number) {

    // resolve empresa currency safely via helper instead of accessing a property that may not exist
    // Rede de seguranca: ja garantida no submit(); sem cotacao nao ha movimento (nunca id generico).
    const cotacao = await this.cotacaoGuard.garantirCotacao({ moeda: this.getEmpresaMoedaPadrao() });
    if (!cotacao) {
      return;
    }

    const movimento: any = {
      id: undefined,
      codigoMovimento: null,
      observacao: 'Movimento inicial da conta ' + conta.tituloDaConta,
      tipoDeTransacao: valor > 0 ? 'RECEITA' : 'DESPESA',
      // Alinhado ao mobile (conta_controller.dart): movimento inicial usa SALDO_INICIAL
      // como tipoReceita (saldo positivo) ou tipoDespesa (saldo negativo).
      ...(valor > 0
        ? { tipoReceita: 'SALDO_INICIAL' }
        : { tipoDespesa: 'SALDO_INICIAL' }),
      tipoMovimento: 'ENTRADA',
      contaId: conta.id,
      valor: valor > 0 ? valor : valor * -1,
      cotacaoId: cotacao.id,
      ...(this.asEnumString(conta.tipoConta) === 'BANCO'
        ? {
          valorRecebidoNaConta: valor > 0 ? valor : valor * -1, // Converte neghativo para positivo
          valorRecebidoEmGuarani: 0,
          valorRecebidoEmDolar: 0,
          valorRecebidoEmReal: 0,
          // Spec 06: campo preparado para ARS; T0 confirmou backend pronto.
          valorRecebidoEmPeso: 0,
        }
        : {
          valorRecebidoNaConta: 0,
          valorRecebidoEmGuarani: this.validaMoedaValor(Moeda.GUARANI),
          valorRecebidoEmDolar: this.validaMoedaValor(Moeda.DOLAR),
          valorRecebidoEmReal: this.validaMoedaValor(Moeda.REAL),
          // Spec 06: campo preparado para ARS; T0 confirmou backend pronto.
          valorRecebidoEmPeso: this.validaMoedaValor(Moeda.PESO),
        }),

      valorTotalMoedaPadrao: valor > 0 ? valor : valor * -1,
      moedaDeOrigem: this.asEnumString(conta.moeda),
      moedaDeDestino: this.asEnumString(conta.moeda),
      emissao: toLocalISOString(),
      cancelado: false,
      baixas: [],
    };

    try {
      await firstValueFrom(this.movimentoService.post('/movimento', movimento));
      this.messageService.add({
        severity: 'success',
        summary: this.translate.instant('common.sucesso'),
        detail: this.translate.instant('Movimento_inicial_criado_com_sucesso'),
      });
    } catch {
      // Toast de erro exibido pelo HttpErrorInterceptor global
    }
  }
  private getEmpresaMoedaPadrao(): Moeda {
    const moeda = this.empresa?.moeda as MoedaDTO | undefined;
    if (moeda) {
      return this.asMoedaEnum(moeda);
    }
    return this.asMoedaEnum(this.form.moeda?.value);
  }

  buildTipoContaOptions(): void {
    this.tipoContaOptions = Object.values(TipoConta)
      .filter(t => t !== TipoConta.DINHEIRO)
      .map(t => ({
        value: t,
        label: this.translate.instant(`enums.tipo_conta.${t.toLowerCase()}`),
      }));
  }

  limparForm() {
    this.form = this.createEmptyForm();
    this.montanteStr = '';
    this.removeInputsEdicaoBanco = false;

    // Voltar titulo para modo criacao
    this.texts.titulo = this.translate.instant('contas.texts.titulo');
    this.texts.subtitulo = this.translate.instant('contas.texts.subtitulo_criar');
  }

  atualizarConta() {
    if (this.submitting) return;
    if (!this.validaCampos()) {
      return;
    }
    const conta: any = {
      id: this.form.id,
      tipoConta: this.form.tipoConta?.value,
      moeda: this.form.moeda?.value,
      tituloDaConta: this.form.tituloDaConta,
      numeroConta: this.form.numeroConta,
      saldo: this.form.saldoInicial,
      valorTotalDeEntradas: this.contaEdicao?.valorTotalDeEntradas,
      valorTotalDeSaidas: this.contaEdicao?.valorTotalDeSaidas,
      descricao: this.form.descricao || this.contaEdicao?.descricao,
      criadoEm: this.contaEdicao?.criadoEm,
      aceitaTarjetaQrcode: this.form.aceitaTarjetaQrcode,
      // Os dados bancarios da empresa nao sao mostrados ao cliente no Mini Shop.
      exibirParaTransferencia: false
    };

    this.submitting = true;
    this.contaService.put(`/conta/${conta.id}`, conta).subscribe({
      next: () => {
        this.submitting = false;
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('common.sucesso'),
          detail: this.translate.instant('Conta_atualizada_com_sucesso'),
        });
        this.limparForm();
        this.loadContas(0, this.rows, this.getCurrentSort());
        this.activeTabIndex = 1;
      },
      error: () => {
        this.submitting = false;
      },
    });

  }

  createEmptyForm(): NovaContaFormDTO {
    const tipoBanco = this.tipoContaOptions?.find(t => t.value === TipoConta.BANCO);
    return {
      tipoConta: tipoBanco || { label: 'Banco', value: TipoConta.BANCO },
      descricao: '',
      moeda: undefined,
      numeroConta: '',
      tituloDaConta: '',
      saldoInicial: null,
      aceitaTarjetaQrcode: false,
    };
  }

  private asEnumString(v: any): string {
    if (!v) return '';

    // Se for um objeto com propriedade 'enum'
    if (typeof v === 'object' && 'enum' in v) {
      return String(v.enum).toUpperCase();
    }

    // Se for uma string, retorna em uppercase
    if (typeof v === 'string') {
      return v.toUpperCase();
    }

    // Se for um objeto do dropdown {label, value}
    if (typeof v === 'object' && 'value' in v) {
      return String(v.value).toUpperCase();
    }

    return String(v ?? '').toUpperCase();
  }

  private asMoedaEnum(value: any): Moeda {
    if (!value) return Moeda.REAL;

    const stringValue = this.asEnumString(value).toUpperCase();

    switch (stringValue) {
      case 'GUARANI':
        return Moeda.GUARANI;
      case 'REAL':
        return Moeda.REAL;
      case 'DOLAR':
        return Moeda.DOLAR;
      case 'PESO':
        return Moeda.PESO;
      default:
        return Moeda.REAL;
    }
  }
  get selectedLocale(): string {
    if (!this.form.moeda) return 'es-PY';
    switch (this.form.moeda.value) {
      case Moeda.REAL: return 'pt-BR';
      case Moeda.DOLAR: return 'en-US';
      case Moeda.GUARANI: return 'es-PY';
      default: return 'es-PY';
    }
  }

  get selectedCasasDecimais(): number {
    if (!this.form.moeda) return 0;
    return this.form.moeda.value === Moeda.GUARANI ? 0 : 2;
  }

  getSelectedCurrencySymbol(): string {
    if (!this.form.moeda) {
      return ''; // Retorna vazio se nenhuma moeda foi selecionada
    }

    return this.getCurrencySymbol(this.form.moeda.value);
  }

  // Método auxiliar para obter símbolo da moeda
  private getCurrencySymbol(moeda: Moeda): string {
    switch (moeda) {
      case Moeda.REAL:
        return 'R$';
      case Moeda.DOLAR:
        return '$';
      case Moeda.GUARANI:
        return 'Gs';
      case Moeda.PESO:
        return '$';
      default:
        return '';
    }
  }

  /**
   * Formata valor monetario aplicando simbolo, locale e casas decimais por moeda.
   * Usado nas colunas de totais/saldo da tabela de contas. ARS delega ao CLDR
   * `es-AR` que produz `$ N,NN` (decisao tech lead 2026-05-06).
   */
  formatarValorMonetario(valor: number | null | undefined, conta: ContaDTO): string {
    if (valor == null || isNaN(valor)) return '-';

    const m: any = (conta as any)?.moeda;
    const moedaStr = String(m?.enum ?? m ?? '').toUpperCase();

    const configuracoes: Record<string, { locale: string; simbolo: string; decimais: boolean }> = {
      'GUARANI': { locale: 'es-PY', simbolo: 'Gs', decimais: false },
      'REAL': { locale: 'pt-BR', simbolo: 'R$', decimais: true },
      'DOLAR': { locale: 'en-US', simbolo: '$', decimais: true },
      'PESO': { locale: 'es-AR', simbolo: '$', decimais: true },
    };

    const config = configuracoes[moedaStr] ?? configuracoes['GUARANI'];
    const options: Intl.NumberFormatOptions = config.decimais
      ? { minimumFractionDigits: 2, maximumFractionDigits: 2 }
      : { minimumFractionDigits: 0, maximumFractionDigits: 0 };

    const valorFormatado = new Intl.NumberFormat(config.locale, options).format(valor);
    return `${config.simbolo} ${valorFormatado}`;
  }
  onMoedaChange(newMoeda: any): void {
    // Quando a moeda muda para GUARANI, remover casas decimais do saldo
    if (newMoeda?.value === Moeda.GUARANI && this.form.saldoInicial != null) {
      this.form.saldoInicial = Math.trunc(this.form.saldoInicial);
    }
    // Reformata a string de exibicao do "Saldo Inicial" para a nova moeda
    // (locale/casas decimais variam por moeda).
    this.montanteStr = this.formatarMontanteParaExibicao(this.form.saldoInicial);
  }

  /**
   * Handler do input "Saldo Inicial" — comportamento estilo calculadora,
   * identico ao input de Valor em /nova-receita: digita "12" -> "0,12".
   * Mantem `form.saldoInicial` (number) sincronizado com `montanteStr`.
   */
  formataCampoValor(event?: any): void {
    const input = event.target as HTMLInputElement;
    const raw = input.value || '';
    const onlyDigits = raw.replace(/\D+/g, '');
    const moeda = this.form.moeda?.value;

    if (!onlyDigits) {
      this.form.saldoInicial = null;
      this.montanteStr = '';
      input.value = '';
      return;
    }

    if (moeda === Moeda.GUARANI) {
      // Guarani: sem centavos, apenas separador de milhar
      const intVal = parseInt(onlyDigits, 10);
      this.form.saldoInicial = intVal;
      const formatted = intVal.toLocaleString('es-PY', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
      this.montanteStr = formatted;
      input.value = formatted;
    } else if (moeda === Moeda.DOLAR) {
      // Dolar: 2 casas decimais, formato 1,234.56
      const cents = parseInt(onlyDigits, 10);
      const val = cents / 100;
      this.form.saldoInicial = val;
      const formatted = val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      this.montanteStr = formatted;
      input.value = formatted;
    } else if (moeda === Moeda.PESO) {
      // Peso argentino: 2 casas decimais, formato 1.234,56
      const cents = parseInt(onlyDigits, 10);
      const val = cents / 100;
      this.form.saldoInicial = val;
      const formatted = val.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      this.montanteStr = formatted;
      input.value = formatted;
    } else {
      // Real: 2 casas decimais, formato 1.234,56
      const cents = parseInt(onlyDigits, 10);
      const val = cents / 100;
      this.form.saldoInicial = val;
      const formatted = val.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      this.montanteStr = formatted;
      input.value = formatted;
    }
  }

  /**
   * Reformata um valor numerico ja existente em `form.saldoInicial` para
   * a string a ser exibida no input. Usado em editarConta() e onMoedaChange().
   */
  private formatarMontanteParaExibicao(valor: number | null | undefined): string {
    if (valor == null || isNaN(valor)) return '';
    const moeda = this.form.moeda?.value;
    if (moeda === Moeda.GUARANI) {
      return Math.trunc(valor).toLocaleString('es-PY', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    }
    if (moeda === Moeda.DOLAR) {
      return valor.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    if (moeda === Moeda.PESO) {
      return valor.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    // Default: REAL
    return valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
}
